import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, Loader2, AlertCircle, ChevronDown, ChevronRight, FileText, Plus, CheckCircle2, X, Trash2, Building2, Filter, SlidersHorizontal } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface Empresa {
  id: number;
  profileName: string;
  legalName: string;
  rfc: string | null;
  isDefault: boolean;
}

interface TransactionLine {
  id: number;
  accountId: number;
  accountCode: string;
  accountName: string;
  debit: string;
  credit: string;
  memo: string | null;
}

interface Transaction {
  id: number;
  date: string;
  description: string;
  reference: string | null;
  status: 'draft' | 'posted' | 'voided';
  sourceModule: string | null;
  totalDebit: string;
  totalCredit: string;
  lines: TransactionLine[];
  createdAt: string;
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  draft: { label: 'Borrador', color: 'bg-yellow-100 text-yellow-800' },
  posted: { label: 'Contabilizado', color: 'bg-green-100 text-green-800' },
  voided: { label: 'Anulado', color: 'bg-red-100 text-red-800' },
};

const formatCurrency = (value: string | number | undefined): string => {
  if (!value) return '$0.00';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '$0.00';
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 2,
  }).format(num);
};

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('es-MX', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export default function TransactionsList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [viewMode, setViewMode] = useState<'expand' | 'modal'>('expand');
  const [postingId, setPostingId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [voidingId, setVoidingId] = useState<number | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  const empresaParam = searchParams.get('empresa');
  const [selectedEmpresaId, setSelectedEmpresaId] = useState<number | null>(
    empresaParam ? parseInt(empresaParam) : null
  );
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [sourceModuleFilter, setSourceModuleFilter] = useState<string>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    const p = searchParams.get('empresa');
    setSelectedEmpresaId(p ? parseInt(p) : null);
  }, [empresaParam]);

  const handleSetEmpresa = (id: number | null) => {
    setSelectedEmpresaId(id);
    const newParams = new URLSearchParams(searchParams);
    if (id) {
      newParams.set('empresa', String(id));
    } else {
      newParams.delete('empresa');
    }
    setSearchParams(newParams);
  };

  const activeFiltersCount = [statusFilter, sourceModuleFilter, dateFrom, dateTo].filter(Boolean).length;

  const { data: empresas = [] } = useQuery<Empresa[]>({
    queryKey: ['finance-emisores'],
    queryFn: async () => {
      const res = await fetch('/api/finance/emisores', { credentials: 'include' });
      if (!res.ok) return [];
      return res.json();
    },
  });

  const { data: transactions, isLoading, error } = useQuery<Transaction[]>({
    queryKey: ['finance-transactions', selectedEmpresaId, statusFilter, sourceModuleFilter, dateFrom, dateTo],
    queryFn: async () => {
      const params = new URLSearchParams({ _t: String(Date.now()), limit: '200' });
      if (selectedEmpresaId) params.set('empresaId', String(selectedEmpresaId));
      if (statusFilter) params.set('status', statusFilter);
      if (sourceModuleFilter) params.set('sourceModule', sourceModuleFilter);
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      const response = await fetch(`/api/finance/transactions?${params}`, {
        credentials: 'include',
        cache: 'no-store',
      });
      if (!response.ok) throw new Error('Error al cargar las transacciones');
      const result = await response.json();
      return Array.isArray(result) ? result : (result.data || []);
    },
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const handleRowClick = (transaction: Transaction) => {
    if (viewMode === 'expand') {
      setExpandedId(expandedId === transaction.id ? null : transaction.id);
    } else {
      setSelectedTransaction(transaction);
    }
  };

  const handlePostTransaction = async (transactionId: number) => {
    try {
      setPostingId(transactionId);
      const response = await fetch(`/api/finance/transactions/${transactionId}/post`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Error al contabilizar la póliza');
      }

      queryClient.invalidateQueries({ queryKey: ['finance-transactions'] });
      
      setSelectedTransaction(null);
    } catch (error: any) {
      console.error('Error posting transaction:', error);
      alert('Error al contabilizar la póliza: ' + error.message);
    } finally {
      setPostingId(null);
    }
  };

  const handleVoidTransaction = async (transactionId: number) => {
    if (!confirm('¿Estás seguro de que deseas anular esta póliza?')) {
      return;
    }
    try {
      setVoidingId(transactionId);
      const response = await fetch(`/api/finance/transactions/${transactionId}/void`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Error al anular la póliza');
      }

      queryClient.invalidateQueries({ queryKey: ['finance-transactions'] });
      
      setSelectedTransaction(null);
    } catch (error: any) {
      console.error('Error voiding transaction:', error);
      alert('Error al anular la póliza: ' + error.message);
    } finally {
      setVoidingId(null);
    }
  };

  const filteredTransactions = transactions?.filter(t =>
    t.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.id.toString().includes(searchTerm)
  ) || [];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/app/finance')}
              className="gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              Volver
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <FileText className="w-6 h-6" />
                Pólizas Contables
              </h1>
              <p className="text-gray-500 text-sm">
                {selectedEmpresaId && empresas.find(e => e.id === selectedEmpresaId)
                  ? `Empresa: ${empresas.find(e => e.id === selectedEmpresaId)?.profileName || empresas.find(e => e.id === selectedEmpresaId)?.legalName}`
                  : 'Vista global — todos los libros'}
                {transactions && ` · ${transactions.length} pólizas`}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <div className="flex border rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('expand')}
                className={`px-3 py-1.5 text-sm ${viewMode === 'expand' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600'}`}
              >
                Expandir
              </button>
              <button
                onClick={() => setViewMode('modal')}
                className={`px-3 py-1.5 text-sm ${viewMode === 'modal' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600'}`}
              >
                Modal
              </button>
            </div>
            <Button onClick={() => navigate('/app/finance/new-entry')} className="gap-2">
              <Plus className="w-4 h-4" />
              Nueva Póliza
            </Button>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="bg-white rounded-lg shadow-sm border p-3 mb-4 space-y-3">
          {/* Empresa pills */}
          {empresas.length > 0 && (
            <div className="flex items-center gap-3 flex-wrap">
              <Building2 className="w-4 h-4 text-indigo-500 shrink-0" />
              <span className="text-sm font-medium text-gray-700 shrink-0">Empresa:</span>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleSetEmpresa(null)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${selectedEmpresaId === null ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  Todas
                </button>
                {empresas.map((emp) => (
                  <button
                    key={emp.id}
                    onClick={() => handleSetEmpresa(emp.id)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${selectedEmpresaId === emp.id ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >
                    {emp.profileName || emp.legalName}
                  </button>
                ))}
              </div>
            </div>
          )}
          {/* Expand/collapse more filters */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 transition-colors"
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              Filtros avanzados
              {activeFiltersCount > 0 && (
                <span className="ml-1 bg-indigo-600 text-white rounded-full text-[10px] w-4 h-4 flex items-center justify-center font-bold">
                  {activeFiltersCount}
                </span>
              )}
            </button>
            {activeFiltersCount > 0 && (
              <button
                onClick={() => { setStatusFilter(''); setSourceModuleFilter(''); setDateFrom(''); setDateTo(''); }}
                className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
              >
                <X className="w-3 h-3" /> Limpiar filtros
              </button>
            )}
          </div>
          {showFilters && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-2 border-t border-gray-100">
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Estado</label>
                <select
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                  className="w-full text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white"
                >
                  <option value="">Todos los estados</option>
                  <option value="draft">Borrador</option>
                  <option value="posted">Contabilizado</option>
                  <option value="voided">Anulado</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Módulo de origen</label>
                <select
                  value={sourceModuleFilter}
                  onChange={e => setSourceModuleFilter(e.target.value)}
                  className="w-full text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white"
                >
                  <option value="">Todos</option>
                  <option value="sales">Ventas</option>
                  <option value="treasury">Tesorería</option>
                  <option value="manual">Manual</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Desde</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={e => setDateFrom(e.target.value)}
                  className="w-full text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Hasta</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={e => setDateTo(e.target.value)}
                  className="w-full text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white"
                />
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-sm border">
          {!isLoading && !error && transactions && transactions.length > 0 && (
            <div className="p-4 border-b">
              <div className="relative">
                <Input
                  placeholder="Buscar por descripción, referencia o ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-4 pr-10"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          )}
          
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12 text-red-600 gap-2">
              <AlertCircle className="w-5 h-5" />
              <span>Error al cargar las transacciones</span>
            </div>
          ) : !filteredTransactions || filteredTransactions.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No hay pólizas registradas</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => navigate('/app/finance/new-entry')}
              >
                Crear primera póliza
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Descripción</TableHead>
                  <TableHead>Referencia</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Estatus</TableHead>
                  <TableHead className="w-28 text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTransactions.map((transaction) => (
                  <>
                    <TableRow
                      key={transaction.id}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => handleRowClick(transaction)}
                    >
                      <TableCell>
                        {viewMode === 'expand' && (
                          expandedId === transaction.id ? (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                          )
                        )}
                      </TableCell>
                      <TableCell className="font-medium">
                        {formatDate(transaction.date)}
                      </TableCell>
                      <TableCell>{transaction.description}</TableCell>
                      <TableCell className="text-gray-500">
                        {transaction.reference || '-'}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(transaction.totalDebit)}
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_LABELS[transaction.status]?.color}`}>
                          {STATUS_LABELS[transaction.status]?.label}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {transaction.status === 'draft' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePostTransaction(transaction.id);
                              }}
                              disabled={postingId === transaction.id}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 disabled:opacity-50"
                              title="Contabilizar póliza"
                            >
                              {postingId === transaction.id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <CheckCircle2 className="w-3 h-3" />
                              )}
                              Contabilizar
                            </button>
                          )}
                          {transaction.status !== 'voided' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleVoidTransaction(transaction.id);
                              }}
                              disabled={voidingId === transaction.id}
                              className="inline-flex items-center justify-center p-1 hover:bg-red-50 rounded text-red-600 hover:text-red-700 disabled:opacity-50"
                              title="Anular póliza"
                            >
                              {voidingId === transaction.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    {viewMode === 'expand' && expandedId === transaction.id && (
                      <TableRow key={`${transaction.id}-details`}>
                        <TableCell colSpan={7} className="bg-gray-50 p-0">
                          <div className="p-4">
                            <h4 className="font-medium text-sm text-gray-700 mb-3">Líneas del Asiento</h4>
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-gray-500 border-b">
                                  <th className="text-left py-2 px-2">Código</th>
                                  <th className="text-left py-2 px-2">Cuenta</th>
                                  <th className="text-right py-2 px-2">Debe</th>
                                  <th className="text-right py-2 px-2">Haber</th>
                                  <th className="text-left py-2 px-2">Memo</th>
                                </tr>
                              </thead>
                              <tbody>
                                {transaction.lines?.map((line) => (
                                  <tr key={line.id} className="border-b last:border-0">
                                    <td className="py-2 px-2 font-mono text-gray-600">{line.accountCode}</td>
                                    <td className="py-2 px-2">{line.accountName}</td>
                                    <td className="py-2 px-2 text-right font-mono text-blue-600">
                                      {parseFloat(line.debit) > 0 ? formatCurrency(line.debit) : '-'}
                                    </td>
                                    <td className="py-2 px-2 text-right font-mono text-green-600">
                                      {parseFloat(line.credit) > 0 ? formatCurrency(line.credit) : '-'}
                                    </td>
                                    <td className="py-2 px-2 text-gray-500">{line.memo || '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr className="font-medium bg-gray-100">
                                  <td colSpan={2} className="py-2 px-2 text-right">Total:</td>
                                  <td className="py-2 px-2 text-right font-mono text-blue-700">
                                    {formatCurrency(transaction.totalDebit)}
                                  </td>
                                  <td className="py-2 px-2 text-right font-mono text-green-700">
                                    {formatCurrency(transaction.totalCredit)}
                                  </td>
                                  <td></td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      <Dialog open={!!selectedTransaction} onOpenChange={() => setSelectedTransaction(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <div className="flex items-start justify-between">
              <div>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Póliza #{selectedTransaction?.id}
                </DialogTitle>
                <DialogDescription>
                  {selectedTransaction?.description}
                </DialogDescription>
              </div>
              <div className="flex gap-2">
                {selectedTransaction?.status === 'draft' && selectedTransaction && (
                  <Button
                    onClick={() => handlePostTransaction(selectedTransaction.id)}
                    disabled={postingId === selectedTransaction.id}
                    className="gap-2"
                    size="sm"
                  >
                    {postingId === selectedTransaction.id ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Contabilizando...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4" />
                        Contabilizar
                      </>
                    )}
                  </Button>
                )}
                {selectedTransaction?.status !== 'voided' && selectedTransaction && (
                  <Button
                    onClick={() => handleVoidTransaction(selectedTransaction.id)}
                    disabled={voidingId === selectedTransaction.id}
                    variant="outline"
                    className="gap-2 text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
                    size="sm"
                  >
                    {voidingId === selectedTransaction.id ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Anulando...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" />
                        Anular
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </DialogHeader>
          
          {selectedTransaction && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Fecha:</span>
                  <p className="font-medium">{formatDate(selectedTransaction.date)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Referencia:</span>
                  <p className="font-medium">{selectedTransaction.reference || '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Estatus:</span>
                  <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${STATUS_LABELS[selectedTransaction.status]?.color}`}>
                    {STATUS_LABELS[selectedTransaction.status]?.label}
                  </span>
                </div>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr className="text-gray-600">
                      <th className="text-left py-3 px-4">Código</th>
                      <th className="text-left py-3 px-4">Cuenta</th>
                      <th className="text-right py-3 px-4">Debe</th>
                      <th className="text-right py-3 px-4">Haber</th>
                      <th className="text-left py-3 px-4">Memo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedTransaction.lines?.map((line) => (
                      <tr key={line.id} className="border-t">
                        <td className="py-3 px-4 font-mono text-gray-600">{line.accountCode}</td>
                        <td className="py-3 px-4">{line.accountName}</td>
                        <td className="py-3 px-4 text-right font-mono text-blue-600">
                          {parseFloat(line.debit) > 0 ? formatCurrency(line.debit) : '-'}
                        </td>
                        <td className="py-3 px-4 text-right font-mono text-green-600">
                          {parseFloat(line.credit) > 0 ? formatCurrency(line.credit) : '-'}
                        </td>
                        <td className="py-3 px-4 text-gray-500">{line.memo || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr className="font-medium border-t">
                      <td colSpan={2} className="py-3 px-4 text-right">Total:</td>
                      <td className="py-3 px-4 text-right font-mono text-blue-700">
                        {formatCurrency(selectedTransaction.totalDebit)}
                      </td>
                      <td className="py-3 px-4 text-right font-mono text-green-700">
                        {formatCurrency(selectedTransaction.totalCredit)}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
